import React, { useState, useEffect, useCallback, useMemo } from "react";
import { 
  ShieldCheck, 
  ChevronDown, 
  ChevronRight, 
  Eye, 
  EyeOff, 
  AlertCircle, 
  Folder, 
  Key, 
  Copy, 
  Check, 
  ExternalLink,
  Lock,
  FileText,
  Search,
  X,
  CreditCard,
  Fingerprint,
  Calendar,
  Clock,
  BookOpen
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import MasterPasswordModal from "./MasterPasswordModal";
import {
  CredentialEntry as DecryptedEntry,
  decryptCredential,
  decryptNotes,
  EncryptedCredential
} from "../../utils/crypto/vault-crypto";
import {
  getVaultsByProgram,
  logCredentialAccess
} from "../../utils/api/endpoints/vault";

// Types
interface ProgramCredentialEntry {
  id: number;
  entryId: number;
  label: string;
  credentialType?: string;
  url?: string;
  username?: string;
  encryptedData: string;
  iv: string;
  authTag: string;
  encryptedNotes?: string;
  notesIv?: string;
  notesAuthTag?: string;
  expiresAt?: string | Date;
}

interface ProgramVaultLink {
  linkId: number;
  vault: {
    vault_id: number;
    name: string;
    description: string;
  };
  accessStatus: "available" | "expired" | "consumed" | "deleted";
  accessSalt: string;
  automated_access_key?: string;
  entries: ProgramCredentialEntry[];
}

const CredentialVault: React.FC<{ programId: number }> = ({ programId }) => {
  const [vaultLinks, setVaultLinks] = useState<ProgramVaultLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unlockPassword, setUnlockPassword] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [expandedVaults, setExpandedVaults] = useState<Set<number>>(new Set());
  const [decryptedEntries, setDecryptedEntries] = useState<Map<number, { entries: DecryptedEntry[], notes?: string }>>(new Map());
  const [pendingEntry, setPendingEntry] = useState<{ entry: ProgramCredentialEntry, salt: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");

  const fetchVaults = useCallback(async () => {
    if (!programId) return;
    setIsLoading(true);
    try {
      const resp = await getVaultsByProgram(programId) as any;
      const data = resp.vaults || resp.credentials || [];
      setVaultLinks(data);
      if (data.length > 0) setExpandedVaults(new Set([data[0].vault?.vault_id]));
    } catch (err: any) {
      setError(err.message || "Connection failure");
    } finally {
      setIsLoading(false);
    }
  }, [programId]);

  useEffect(() => { fetchVaults(); }, [fetchVaults]);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied", { duration: 1500 });
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDecrypt = async (entry: ProgramCredentialEntry, salt: string, pass?: string) => {
    const decryptionPass = pass || vaultLinks.find(l => l.entries.some(e => e.id === entry.id))?.automated_access_key;
    if (!decryptionPass) {
      setPendingEntry({ entry, salt });
      setShowPasswordModal(true);
      return;
    }
    
    try {
      const encryptedData: EncryptedCredential = {
        encrypted_data: entry.encryptedData,
        iv: entry.iv,
        auth_tag: entry.authTag,
        salt,
        key_iterations: 600000
      };
      const decrypted = await decryptCredential(encryptedData, decryptionPass);
      let notes: string | undefined;
      if (entry.encryptedNotes && entry.notesIv && entry.notesAuthTag) {
        notes = await decryptNotes(entry.encryptedNotes, entry.notesIv, entry.notesAuthTag, decryptionPass, salt, 600000);
      }
      setDecryptedEntries(prev => new Map(prev).set(entry.id, { entries: decrypted.entries, notes }));
      const link = vaultLinks.find((l: ProgramVaultLink) => l.entries.some(e => e.id === entry.id));
      if (link?.vault?.vault_id) {
        await logCredentialAccess(link.vault.vault_id, "decrypt", {
          programId,
          automatedAccessKey: pass || undefined
        });
        if (pass && !link.automated_access_key) {
          link.automated_access_key = pass;
        }
      }
    } catch {
      if (pass) setUnlockPassword(null);
      // If automated decryption failed, we might need to ask for password as fallback
      if (!pass) {
        setPendingEntry({ entry, salt });
        setShowPasswordModal(true);
      } else {
        throw new Error("Invalid access password");
      }
    }
  };

  const filteredVaults = useMemo(() => {
    if (!globalSearchTerm) return vaultLinks;
    const term = globalSearchTerm.toLowerCase();
    return vaultLinks.map(link => ({
      ...link,
      entries: link.entries.filter(e => 
        e.label?.toLowerCase().includes(term) || 
        e.username?.toLowerCase().includes(term) || 
        e.url?.toLowerCase().includes(term)
      )
    })).filter(link => link.entries.length > 0);
  }, [vaultLinks, globalSearchTerm]);

  const ensureAbsoluteUrl = (url: string) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `https://${url}`;
  };

  if (isLoading) return <div className="p-6 text-gray-400 font-medium animate-pulse">Synchronizing environment...</div>;

  return (
    <div className="text-left w-full max-w-6xl transition-all duration-500">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheck className="w-7 h-7 text-blue-600" />
        <div>
          <h2 className="text-lg font-bold text-gray-900 leading-none">Program Credentials</h2>
          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">End-to-end encrypted sharing</p>
        </div>
      </div>

      {/* Global Search - Wider and more prominent */}
      {vaultLinks.length > 0 && (
        <div className="mb-6 relative group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
          <input 
            type="text"
            placeholder="Search across all vaults & credentials..."
            value={globalSearchTerm}
            onChange={(e) => setGlobalSearchTerm(e.target.value)}
            className="w-full h-12 pl-12 pr-10 bg-white border border-gray-100 focus:border-blue-200 focus:ring-4 focus:ring-blue-50/30 rounded-2xl text-sm transition-all shadow-sm outline-none"
          />
          {globalSearchTerm && (
            <button onClick={() => setGlobalSearchTerm("")} className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {error ? (
        <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      ) : filteredVaults.length === 0 ? (
        <div className="p-16 text-center border-2 border-dashed border-gray-100 rounded-[32px] bg-gray-50/30 transition-all">
          <p className="text-sm text-gray-400 font-semibold">{globalSearchTerm ? "No matching credentials found" : "The program owner has not shared any credentials yet."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredVaults.map((link: ProgramVaultLink) => {
            const vaultId = link.vault?.vault_id;
            const isExpanded = expandedVaults.has(vaultId);
            return (
              <div key={link.linkId} className="border border-gray-100 rounded-2xl bg-white overflow-hidden transition-all shadow-sm hover:border-gray-200">
                <div 
                  className={`flex items-center justify-between p-3.5 cursor-pointer hover:bg-gray-50/50 transition-colors ${isExpanded ? 'bg-gray-50/50' : ''}`}
                  onClick={() => {
                    const id = link.vault?.vault_id;
                    setExpandedVaults(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${isExpanded ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600'}`}>
                      <Folder className="w-4.5 h-4.5" />
                    </div>
                    <div>
                      <h4 className="text-[13px] font-bold text-gray-900">{link.vault.name}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{link.entries?.length} {link.entries?.length === 1 ? 'Credential' : 'Credentials'}</span>
                        {link.accessStatus !== "available" && (
                          <span className="px-1.5 py-0.5 rounded-lg bg-red-100 text-red-600 text-[8px] font-black uppercase tracking-widest">{link.accessStatus}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <motion.div animate={{ rotate: isExpanded ? 90 : 0 }}>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </motion.div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
                      <div className="p-3 pt-0 space-y-2">
                        {link.entries.map((entry: ProgramCredentialEntry) => {
                          const isRevealed = decryptedEntries.has(entry.id);
                          return (
                            <div key={entry.id} className={`border border-gray-50 rounded-xl bg-gray-50/10 p-3 transition-all ${isRevealed ? 'ring-1 ring-blue-100/50' : ''}`}>
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${isRevealed ? 'bg-blue-50 text-blue-600' : 'bg-white border border-gray-100 text-gray-400'}`}>
                                    {entry.credentialType === 'password' ? <Key className="w-3.5 h-3.5" /> : <Fingerprint className="w-3.5 h-3.5" />}
                                  </div>
                                  <div className="min-w-0">
                                    <h5 className="text-[12px] font-bold text-gray-900 truncate leading-none mb-1">{entry.label}</h5>
                                    <div className="flex items-center gap-2">
                                      {entry.username && <span className="text-[9px] font-bold text-gray-400 tracking-tighter uppercase">{entry.username}</span>}
                                      {entry.url && (
                                        <a 
                                          href={ensureAbsoluteUrl(entry.url)} 
                                          target="_blank" 
                                          rel="noreferrer" 
                                          className="text-[9px] text-blue-600 hover:underline flex items-center gap-1 font-black uppercase truncate max-w-[150px]"
                                          title={entry.url}
                                        >
                                          <ExternalLink className="w-2 h-2 flex-shrink-0" /> {entry.url.replace(/^https?:\/\//, '')}
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => isRevealed ? setDecryptedEntries(p => { const n = new Map(p); n.delete(entry.id); return n; }) : handleDecrypt(entry, link.accessSalt, unlockPassword || undefined)}
                                  className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-[0.1em] transition-all flex items-center gap-1.5 ${isRevealed ? 'bg-gray-100 text-gray-600' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm shadow-blue-100'}`}
                                >
                                  {isRevealed ? <><EyeOff className="w-3 h-3" /> Hide</> : <><Eye className="w-3 h-3" /> Reveal</>}
                                </button>
                              </div>

                              {isRevealed && (
                                <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="mt-3 space-y-3">
                                  {/* Side-by-side Grid: optimized for wider layout (3 columns on desktop) */}
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {decryptedEntries.get(entry.id)?.entries.map((f, i) => (
                                      <div key={i} className="bg-white border border-gray-100 p-2.5 rounded-xl shadow-sm hover:border-blue-100 transition-colors group relative">
                                        <div className="flex items-center justify-between mb-0.5">
                                          <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest leading-none">{f.key}</span>
                                          <button onClick={() => handleCopy(f.value, `${entry.id}-${i}`)} className="text-gray-300 hover:text-blue-600 transition-all">
                                            {copiedId === `${entry.id}-${i}` ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                                          </button>
                                        </div>
                                        <code className="text-xs font-mono font-bold text-gray-900 select-all block truncate pr-4">{f.value}</code>
                                      </div>
                                    ))}
                                    
                                    {/* Expiry Details - Shows if present */}
                                    {entry.expiresAt && (
                                      <div className="bg-gray-50/50 border border-gray-100 p-2.5 rounded-xl shadow-sm flex flex-col justify-center">
                                        <div className="flex items-center gap-1.5 text-[8px] font-black text-amber-600 uppercase tracking-widest mb-1 leading-none">
                                          <Calendar className="w-2.5 h-2.5" /> Expiry
                                        </div>
                                        <span className="text-[11px] font-bold text-gray-700">
                                          {new Date(entry.expiresAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Secure Notes - Shows if present after decryption */}
                                  {decryptedEntries.get(entry.id)?.notes && (
                                    <div className="bg-blue-50/40 border border-blue-100/30 p-3.5 rounded-xl relative overflow-hidden group">
                                      <FileText className="absolute -right-1 -bottom-1 w-12 h-12 text-blue-100 opacity-20 transition-transform group-hover:rotate-6" />
                                      <div className="flex items-center gap-1.5 text-[8px] font-black text-blue-500 uppercase tracking-widest mb-1.5 leading-none">
                                        <Lock className="w-2.5 h-2.5" /> Secure Notes
                                      </div>
                                      <p className="text-[11px] text-blue-900 font-medium leading-relaxed whitespace-pre-wrap relative z-10">{decryptedEntries.get(entry.id)?.notes}</p>
                                    </div>
                                  )}
                                </motion.div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}

      {/* Security Info Tag removed from here as it was moved to the top */}

      <MasterPasswordModal
        isOpen={showPasswordModal}
        onClose={() => { setShowPasswordModal(false); setPendingEntry(null); }}
        onSubmit={async (pass) => { 
          setUnlockPassword(pass); 
          setShowPasswordModal(false); 
          if (pendingEntry) await handleDecrypt(pendingEntry.entry, pendingEntry.salt, pass); 
          setPendingEntry(null); 
        }}
        mode="unlock"
        title="Session Security"
        label="Vault Access Key"
        placeholder="ENTER KEY..."
        description="Verify your identity to decrypt shared assets locally in your browser."
      />
    </div>
  );
};

export default CredentialVault;
